import { useEffect } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useUIv2 } from '@/hooks/useUIv2'
import { escapeBelongsToInput } from '@/components/previews/escapeBelongsToInput'
import DocumentPreview from '@/routes/IntelligentBoard/DocumentPreviewShape/DocumentPreview'
import { BoardErrorBoundary } from '@/routes/IntelligentBoard/BoardErrorBoundary'
import { boardOverlayInsetStartPx } from '@/routes/IntelligentBoard/boardLeftNavLayout'

interface SourceFilePreviewOverlayProps {
  documentId: string
  name: string
  /**
   * The tag the row is about, as this file spells it. The board's viewers land
   * on it: a sheet highlights its column in amber and scrolls to it, a PDF or
   * a drawing opens already searching for it.
   */
  highlightTag?: string
  /** A P&ID graph's node to box, when `documentId` is that graph. */
  highlightNodeId?: string | null
  onClose: () => void
}

/**
 * A Source file cell's file, in the SAME preview a board card opens: the
 * backdrop, panel size and `DocumentPreview` are copied from `BoardCanvas`'s
 * preview modal, so every file type renders with the board's viewer. No
 * Remove from Board — there is no card to remove here.
 *
 * Two things the board gets from its surroundings are set here explicitly:
 * - the backdrop starts after the 80px app rail, as the board's does with its
 *   rail open (this page always mounts `AppSidebar`), so the panel centres on
 *   the same area;
 * - the 12px base font size tldraw's `.tl-container` gives everything inside
 *   the board, which the preview's unsized text inherits there.
 */
export default function SourceFilePreviewOverlay({
  documentId,
  name,
  highlightTag,
  highlightNodeId,
  onClose,
}: SourceFilePreviewOverlayProps) {
  const isV2 = useUIv2()

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // A search box inside the panel owns its own Escape; see the helper.
      if (e.key === 'Escape' && !escapeBelongsToInput(e)) onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  return (
    <div
      className="fixed inset-y-0 end-0 z-[9999999] flex items-center justify-center bg-black/40"
      style={{
        insetInlineStart: boardOverlayInsetStartPx(true, true),
        ...(isV2 ? { padding: 32 } : {}),
      }}
      onClick={onClose}
      data-testid="source-file-preview"
    >
      <div
        className={
          isV2
            ? 'bg-white dark:bg-dark-card rounded-xl shadow-2xl flex flex-col overflow-hidden w-full max-w-4xl h-[80vh] relative'
            : 'bg-white dark:bg-dark-card rounded-xl shadow-2xl flex flex-col overflow-hidden relative w-full h-full'
        }
        style={{ fontSize: 12, ...(!isV2 ? { margin: 16 } : {}) }}
        onClick={(e) => e.stopPropagation()}
      >
        <Button variant="ghost" size="sm" onClick={onClose} className="absolute top-2 right-2 z-10">
          <X className="w-4 h-4" />
        </Button>
        <BoardErrorBoundary>
          <div className="flex-1 min-h-0 overflow-hidden">
            <DocumentPreview
              documentId={documentId}
              name={name}
              showFooter
              highlightTag={highlightTag}
              highlightNodeId={highlightNodeId}
              // A drawing opens as the board shows a P&ID card: its graph.
              openDrawingAsGraph
              // The board scales its title with canvas zoom (11px at 100%,
              // ~14px at the zoom a board is usually read at); with no canvas
              // here it would sit at 11px and read small beside it.
              titleFontSizePx={14}
              width={undefined}
              height={undefined}
            />
          </div>
        </BoardErrorBoundary>
      </div>
    </div>
  )
}
